export enum LabtestOrderStatus {
  PENDING = "payment_pending",
  PENDING_ADMIN = "pending_admin",
  APPROVAL_PENDING = "approval_pending",
  CLINIC_ASSIGNED = "clinic_assigned",
  PICKUP_ASSIGNED = "pickup_assigned",
  PICKUP_COMPLETED = "pickup_completed",
  MOVED_TO_LAB = "moved_to_lab",
  REPORT_GENERATED = "report_generated",
  CANCELLED = "cancelled",
}

export enum LabtestTypeEnum {
  SINGLE_TEST = "single",
  PANEL_TEST = "panel",
  PROFILE_TEST = "profile",
  HEALTH_PACKAGE = "health_package",
}

export interface LabtestOrderListing {
  currentPage: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  labtests: LabtestOrderDetails[];
}

export interface LabtestOrderDetails {
  billUrl: any;
  paymentMethod: string;
  paymentSummary: any;
  id: number;
  patientId: string;
  patientPhoneNumber: string;
  orderNumber: string;
  orderDate: string; // ISO Date string
  orderType: "direct" | "online" | string;
  orderStatus: "payment_pending" | "completed" | "cancelled" | string;
  orderTotal: string;
  paymentId: string | null;
  paymentStatus: "pending" | "success" | "failed" | string;
  prescriptionRequired: boolean;
  hasPrescription: boolean;
  isLabTestOrder: boolean;
  clinicId: string;
  deliveryPersonId: string;
  orderAddress: string;
  orderPincode: string;
  doctorName: string | null;
  orderNote: string | null;
  createdAt: string;
  note?: string;
  labtestItemsDetails: LabtestItemsDetails;
  labtestPrescriptionDetails: any[];
  labtestReportDetails: any[];
  labtestTimelineDetails: LabtestTimelineDetail[];

  clinicDetails: ClinicDetails;
  patientDetails: PatientDetails;
  deliveryPersonDetails: DeliveryPersonDetails;
}

// ------------------ Nested Interfaces ------------------ //

export interface LabtestItemsDetails {
  labTestDetails: LabTestDetails;
  price: string;
  timeSlot: string;
  date: string;
  patientId: string;
}

export interface LabTestDetails {
  id: number;
  name: string;
  description: string;
  type: "single" | "group";
  icon: string;
  includedTests: string;
  reportTime: string;
  originalPrice: number;
  offerPrice: number;
  discountPercentage: number;
  gender: "male" | "female" | "both";
  ageLimitMin: number | null;
  ageLimitMax: number | null;
  fastingRequired: boolean;
  sampleType: string;
  isPublished: boolean;
  labTestCategory: string;
  isHealthPackage: boolean;
  preparationDetails: PreparationDetail[];
  includedGroupLabtests: IncludedGroupLabtest[];
  includedSingleLabtests: IncludedSingleLabtest[];
}

export interface PreparationDetail {
  id: number;
  labtestId: number;
  preparation: string;
  description: string;
}

export interface IncludedGroupLabtest {
  id: number;
  name: string;
  description: string;
  totalIncludeTests: number;
  tests: IncludedSingleLabtest[];
  sampleType: string;
}

export interface IncludedSingleLabtest {
  id: number;
  name: string;
  description: string;
  type: "single";
}

export interface LabtestTimelineDetail {
  timelineId: number;
  timelineStatus: string;
  timelineTimestamp: string;
}

export interface ClinicDetails {
  clinicName: string;
  clinicProfileImage: string | null;
  clinicSupervisorName: string;
  clinicAddress: string;
  clinicPincode: string;
  clinicMobileNumber: string;
  drugLicenseNumber: string;
}

export interface PatientDetails {
  patientName: string;
  patientProfileImage: string;
  patientMobileNumber: string;
  patientAddress: string;
  patientPincode: string;
}

export interface DeliveryPersonDetails {
  deliveryPersonName: string;
  deliveryPersonProfileImage: string;
  deliveryPersonMobileNumber: string;
  deliveryPersonVehicleNumber: string;
}

export interface LabTestType {
  id: number;
  name: string;
  description: string;
}

export interface LabTestCategory {
  id: number;
  name: string;
  icon: string;
  isHealthPackage: boolean;
  isDailyRoutineTest: boolean;
}

export interface LabTestCategoriesResponse {
  success: boolean;
  message: string;
  data: LabTestCategory[];
}

export interface Precaution {
  id: string;
  description: string;
}

export interface Variant {
  id: number;
  testId: number;
  type: "single" | "group" | "panel" | "profile";
  name: string;
  description: string;
  includedTests?: number;
}

export interface LabTest {
  name: string;
  description: string;
  otherNames: string;
  price: string;
  offerPrice: string;
  discount: string;
  reportTime: string;
  ageLimitMin: string;
  ageLimitMax: string;
  gender: string;
  sampleType: string;
  fasting: boolean;
  showOnHome: boolean;
  isHealthPackage: boolean;
  icon: string;
  labTestCategory: string;
}

export interface LabtestTypesResponse {
  success: boolean;
  message: string;
  data: {
    currentPage: number;
    itemsPerPage: number;
    totalItemsCount: number;
    totalPages: number;
    nextPage: number;
    previousPage: number;
    labtestTypesData: LabTestType[];
  };
}

// More specific version with string literal types
export interface LabTestOptionsResponse {
  success: boolean;
  message: string;
  data: LabTestOptionsData;
}

export interface LabTestOptionsData {
  currentPage: number;
  itemsPerPage: number;
  totalItemsCount: number;
  totalPages: number;
  nextPage: number;
  previousPage: number;
  labTestDetails: LabTestOptionsDetail[];
}

export interface LabTestOptionsDetail {
  id: number;
  name: string;
  description: string;
  type: "group" | "single";
  icon: string;
  includedTestsCount: string;
  includedTests: LabTestType[];
}

export interface SelectedGroupTests {
  id: number;
  name: string;
  description: string;
  includedTestsCount: string;
  includedTests: IncludedTestOptions[];
  type?: "panel" | "profile";
}

export interface ListingApiResponse {
  success: boolean;
  message: string;
  data: LabTestData;
}

interface LabTestData {
  currentPage: number;
  itemsPerPage: number;
  totalItemsCount: number;
  totalPages: number;
  nextPage: number | null;
  previousPage: number | null;
  labTestDetails: LabTestListingDetail[];
}

export interface LabTestListingDetail {
  id: number;
  name: string;
  description: string;
  otherNames: string;
  type: "group" | "single";
  icon: string;
  includedTests: string;
  reportTime: string;
  originalPrice: number;
  offerPrice: number;
  discountPercentage: number;
  gender: "both" | "male" | "female";
  ageLimitMin: number | null;
  ageLimitMax: number | null;
  fastingRequired: boolean;
  sampleType: string;
  isPublished: boolean;
  labTestCategory: string;
  isHealthPackage: boolean;
  preparationDetails: preparationDetails[];
  includedGroupLabtests: IncludedGroupTest[];
  includedSingleLabtests: IncludedSingleTest[];
}

export interface LabtestListingData {
  id: number;
  name: string;
  description: string;
  otherNames: string;
  type: LabtestTypeEnum;
  reportTime: string;
  originalPrice: number;
  offerPrice: number;
  gender: string;
  fastingRequired: boolean;
  sampleType: string;
  ageLimitMin: null;
  ageLimitMax: null;
  discountPercentage: number;
  isPublished: boolean;
  labTestCategory: string;
  isHealthPackage: boolean;
  includedTests: string;
}

export interface labtestListingResponse {
  success: boolean;
  message: string;
  data: {
    currentPage: number;
    itemsPerPage: number;
    totalItemsCount: number;
    totalPages: number;
    nextPage: number;
    previousPage: number;
    labTestDetails: LabtestListingData[];
  };
}

interface preparationDetails {
  id: number;
  labtestId: number;
  preparation: string;
  description: string;
}

interface IncludedSingleTest {
  id: number;
  name: string;
  description: string;
  type: "single" | "group";
}

interface IncludedGroupTest {
  id: number;
  name: string;
  description: string;
  totalIncludeTests: number;
  sampleType: string;
  tests: GroupTest[];
}

interface GroupTest {
  id: number;
  name: string;
  description: string;
  type: "group" | "single";
}

export interface DriverListApiResponse {
  success: boolean;
  data: Driver[];
}

export interface Driver {
  id: string;
  name: string;
  mobileNumber: string;
  profilePicture: string;
  lastOrderTime: string; // ISO 8601 date string
  pendingOrdersCount: number;
}

export interface LabTestByIdApiResponse {
  success: boolean;
  message: string;
  data: LabTestByIdData;
}
export type LabTestTypeInterface =
  | "single"
  | "panel"
  | "profile"
  | "health_package";

export interface LabTestByIdData {
  id: number;
  name: string;
  description: string;
  otherNames: string;
  type: LabTestTypeInterface;
  reportTime: string;
  originalPrice: number;
  offerPrice: number;
  discountPercentage: number;
  gender: "male" | "female" | "both";
  fastingRequired: boolean;
  sampleType: string;
  ageLimitMin: number | null;
  ageLimitMax: number | null;
  isPublished: boolean;
  labTestCategory: string;
  isHealthPackage: boolean;
  includedTests: IncludedTest[];
  precautions: {
    id: number;
    description: string;
  }[]
}
export type IncludedTest = SingleChild | PanelChild | ProfileChild;

export interface SingleChild {
  type: "single";
  id: number;
  name: string;
  description?: string;
  includedTests?: any[];
}

export interface PanelChild {
  type: "panel";
  id: number;
  name: string;
  singles?: SingleChild[];
  includedTests?: SingleChild[];
  description?: string;
}

export interface ProfileChild {
  type: "profile";
  id: number;
  name: string;
  includedTests?: (PanelChild | SingleChild)[];
  singles?: SingleChild[];
  description?: string;
}

export interface IncludedTestPayload {
  includedType: "single" | "panel" | "profile" | "health_package";
  singleTestId: number;
  groupTestId: number | null;
  profileTestId: number | null;
}

export interface LabTestListingItem {
  id: number;
  name: string;
  type: "single" | "panel" | "profile";
  description: string;
  includedTests: LabTestListingItem[];
  singles?: LabTestListingItem[];
}

export interface LabTestOptionsListingResponse {
  success: boolean;
  message: string;
  data: LabTestOptionsListingData;
}

export interface LabTestOptionsListingData {
  currentPage: number;
  itemsPerPage: number;
  totalItemsCount: number;
  totalPages: number;

  nextPage: number | null;
  previousPage: number | null;

  labTestDetails: LabTestListItem[];
}

export interface LabTestListItem {
  id: number;
  name: string;
  description: string;
  type: LabTestType;
  includedTests: IncludedTestOptions[];
}
export type IncludedTestOptions =
  | SingleChildOption
  | PanelChildOption
  | ProfileChildOption;
export interface SingleChildOption {
  type?: "single";
  id: number;
  name: string;
  description?: string;
}

export interface PanelChildOption {
  type?: "panel";
  id: number;
  name: string;
  description?: string;
  singles: SingleChild[];
}

export interface ProfileChildOption {
  type?: "profile";
  id: number;
  name: string;
  description?: string;
  includedTests: (PanelChildOption | SingleChildOption)[];
}
